import { createContext, useContext, useRef } from 'react';
import { createDynamicEntityStore } from './builder_store';
import { createSitemapStore } from './sitemap_store';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useStore } from 'zustand';

/**
 * This context is used to supply data from the CMS to the Page Builder editor only. They are immutable and are only for reference.
 */
export const DynamicEntityCMSDataContext = createContext(null);
export const useDynamicEntityCMSDataContext = () => {
  return useContext(DynamicEntityCMSDataContext);
};

/**
 * This context is used to supply the data from the CMS to the Page Builder sitemap only.
 */
export const SitemapContext = createContext(null);

export const useSitemapStoreContext = (selector) => {
  const context = useContext(SitemapContext);

  if (!context) {
    throw new Error('useSitemapStoreContext must be used within a SitemapProvider');
  }

  return useStore(context, selector);
};

export const SitemapStoreProvider = ({ children, allSitePages, categoriesWithTemplates }) => {
  const storeRef = useRef(null);

  if (storeRef.current === null) {
    storeRef.current = createSitemapStore(undefined, allSitePages, categoriesWithTemplates);
  }

  return <SitemapContext.Provider value={storeRef.current}>{children}</SitemapContext.Provider>;
};

/**
 * This is used to supply a Zustand + ImmerJS store to the Page Builder editor.
 */
const DynamicEntityStoreContext = createContext(null);

export const useDynamicEntityStoreContext = (selector, equalityFn) => {
  const context = useContext(DynamicEntityStoreContext);

  if (!context) {
    throw new Error('useDynamicEntityStoreContext must be used within a DynamicEntityStoreProvider');
  }

  return useStoreWithEqualityFn(context, selector, equalityFn);
};

export const DynamicEntityStoreProvider = ({ children, dynamicEntity, isModule }) => {
  const storeRef = useRef(null);

  if (storeRef.current === null) {
    storeRef.current = createDynamicEntityStore(dynamicEntity, isModule);
  }

  return <DynamicEntityStoreContext.Provider value={storeRef.current}>{children}</DynamicEntityStoreContext.Provider>;
};
